import React, { useEffect, useMemo, useRef, useState } from "react";
import { useTheme } from "../../lib/theme";
import { cn } from "../../lib/utils";
import type { OfficerPerformanceRow } from "../../services/patrolReports";
import { useHospitalColors } from "../../utils/hospitalColors";

/**
 * Officer distribution — a histogram with a fitted normal (bell) curve and
 * percentile markers over the "Recorded Activity by Officer" set, for a chosen
 * metric. The selected metric is lifted to the parent so the officer table can
 * show each officer's percentile for the same metric.
 *
 * Framing note: like the rest of Patrol Reports, this shows the recorded scan
 * activity for a supervisor's judgement — a spread, not a score or ranking.
 */

export type MetricKey = "scansPerHour" | "scans" | "checkpoints" | "shifts" | "shiftHours";

export interface MetricDef {
  key: MetricKey;
  label: string;
  dec: number;
  unit: string;
  color: string; // brand color: bar fill + active button
  textOn: string; // text color on the active (filled) button
  textCol: string; // readable text color for the inactive label
  value: (o: OfficerPerformanceRow) => number | null;
}

// Coerce to a finite number or null. Postgres returns numeric columns (e.g.
// scans_per_hour = ROUND(…,1)) as STRINGS, so every metric value is normalized
// here — otherwise downstream `.toFixed()` / arithmetic would break on a string.
const num = (v: unknown): number | null => {
  if (v == null || v === "") return null;
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
};

export const OFFICER_METRICS: MetricDef[] = [
  { key: "scansPerHour", label: "Scans / hr", dec: 1, unit: "/hr", color: "#3B7BBE", textOn: "#fff", textCol: "#2F6DA8", value: (o) => num(o.scans_per_hour) },
  { key: "scans", label: "Scans", dec: 0, unit: "", color: "#EE3E2E", textOn: "#fff", textCol: "#CF2A1E", value: (o) => num(o.scans) },
  { key: "checkpoints", label: "Checkpoints", dec: 0, unit: "", color: "#C9A227", textOn: "#1F2937", textCol: "#8A6D0E", value: (o) => num(o.distinct_checkpoints) },
  { key: "shifts", label: "Shifts", dec: 0, unit: "", color: "#046C98", textOn: "#fff", textCol: "#046C98", value: (o) => num(o.shifts) },
  { key: "shiftHours", label: "Shift Time", dec: 1, unit: "h", color: "#EA6D24", textOn: "#fff", textCol: "#C2551A", value: (o) => { const m = num(o.shift_minutes); return m == null ? null : m / 60; } },
];

export function metricDef(key: MetricKey): MetricDef {
  return OFFICER_METRICS.find((m) => m.key === key) ?? OFFICER_METRICS[0];
}

export function fmtMetric(v: number, dec: number): string {
  const n = Number(v); // defensive: never call .toFixed on a string
  if (!Number.isFinite(n)) return "—";
  return dec ? n.toFixed(dec) : Math.round(n).toString();
}

export function ordinal(n: number): string {
  const s = ["th", "st", "nd", "rd"];
  const v = Math.round(n) % 100;
  return Math.round(n) + (s[(v - 20) % 10] || s[v] || s[0]);
}

/** Mid-rank percentile of v within an ascending-sorted array (0–100). */
export function percentileRankOf(sortedAsc: number[], v: number): number {
  if (sortedAsc.length === 0) return 0;
  let below = 0;
  let eq = 0;
  for (const x of sortedAsc) {
    if (x < v) below++;
    else if (x === v) eq++;
  }
  return ((below + 0.5 * eq) / sortedAsc.length) * 100;
}

export function pctStyle(p: number): { bg: string; fg: string; lab: string } {
  if (p >= 90) return { bg: "#0B3D66", fg: "#fff", lab: "Top 10%" };
  if (p >= 75) return { bg: "#1E5B8C", fg: "#fff", lab: "75–90" };
  if (p >= 50) return { bg: "#3B7BBE", fg: "#fff", lab: "50–75" };
  if (p >= 25) return { bg: "#9DC0E0", fg: "#0B1F3A", lab: "25–50" };
  return { bg: "#E1ECF6", fg: "#0B1F3A", lab: "Bottom 25%" };
}

function quantile(sorted: number[], q: number): number {
  const n = sorted.length;
  if (n === 0) return 0;
  if (n === 1) return sorted[0];
  const pos = (n - 1) * q;
  const lo = Math.floor(pos);
  const hi = Math.ceil(pos);
  return lo === hi ? sorted[lo] : sorted[lo] + (sorted[hi] - sorted[lo]) * (pos - lo);
}

// ── Hospital-comparison overlays ─────────────────────────────────────────────
// The Distribution plot receives the manager's already-scoped officer set, and
// each officer carries its hospital. We can therefore overlay one fitted curve
// per hospital (plus a pooled "All my locations" series) with no backend call.
const ALL_KEY = "__ALL__";

/** Summary statistics for one series of metric values. */
function statsOf(vals: number[]) {
  const sorted = [...vals].sort((a, b) => a - b);
  const n = sorted.length;
  const mean = n ? vals.reduce((a, b) => a + b, 0) / n : 0;
  const variance = n ? vals.reduce((a, b) => a + (b - mean) ** 2, 0) / n : 0;
  const q = [0.1, 0.25, 0.5, 0.75, 0.9].map((x) => quantile(sorted, x));
  return { n, mean, std: Math.sqrt(variance), min: sorted[0] ?? 0, max: sorted[n - 1] ?? 0, sorted, q };
}

export function OfficerDistribution({
  officers,
  metric,
  onMetricChange,
}: {
  officers: OfficerPerformanceRow[];
  metric: MetricKey;
  onMetricChange: (m: MetricKey) => void;
}) {
  const [bins, setBins] = useState(10);
  const [curve, setCurve] = useState(true);
  const [open, setOpen] = useState(true);
  // Which Summary statistic is hovered — highlighted on the plot.
  const [hl, setHl] = useState<string | null>(null);
  // Hospital-comparison overlays: off by default (single pooled distribution).
  const [compareOn, setCompareOn] = useState(false);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [focus, setFocus] = useState<string>(ALL_KEY);
  const [themeMode] = useTheme();
  const isDark = themeMode === "dark";
  const md = metricDef(metric);

  // Hover-only: a bar reveals which officers fall in it while the cursor is
  // over the plot, and the card disappears as soon as the cursor leaves.
  // Positions are in container pixels.
  const wrapRef = useRef<HTMLDivElement>(null);
  type Hit = { bin: number; left: number; top: number; w: number };
  const [hover, setHover] = useState<Hit | null>(null);
  const active = hover;
  const posFromEvent = (e: React.MouseEvent) => {
    const r = wrapRef.current?.getBoundingClientRect();
    if (!r) return { left: 0, top: 0, w: 600 };
    return { left: e.clientX - r.left, top: e.clientY - r.top, w: r.width };
  };
  // Bin indices change when the metric, bin count, or officer set changes.
  useEffect(() => {
    setHover(null);
  }, [metric, bins, officers, compareOn, focus, selected]);

  const vals = useMemo(
    () => officers.map((o) => md.value(o)).filter((v): v is number => v != null && !isNaN(v)),
    [officers, metric] // eslint-disable-line react-hooks/exhaustive-deps
  );

  const S = useMemo(() => {
    const sorted = [...vals].sort((a, b) => a - b);
    const n = sorted.length;
    const mean = n ? vals.reduce((a, b) => a + b, 0) / n : 0;
    const variance = n ? vals.reduce((a, b) => a + (b - mean) ** 2, 0) / n : 0;
    return { n, mean, std: Math.sqrt(variance), min: sorted[0] ?? 0, max: sorted[n - 1] ?? 0, sorted };
  }, [vals]);

  const q = useMemo(() => [0.1, 0.25, 0.5, 0.75, 0.9].map((x) => quantile(S.sorted, x)), [S.sorted]);

  const text = isDark ? "#E5E7EB" : "#334155";
  const muted = isDark ? "#9AA3AF" : "#6B7280";
  const grid = isDark ? "rgba(255,255,255,0.10)" : "#EEF1F5";
  const axis = isDark ? "#4B5563" : "#CBD2DC";
  const curveColor = isDark ? "#CBD5E1" : "#0B1F3A";

  // ── geometry ──
  const W = 840;
  const H = 430;
  const m = { t: 36, r: 20, b: 52, l: 52 };
  const iw = W - m.l - m.r;
  const ih = H - m.t - m.b;

  // Shared canonical hospital→color map (same source as the Scan Map / calendar).
  const hospitalColors = useHospitalColors();

  // Distinct hospitals present in this (already-scoped) officer set.
  const hospitals = useMemo(() => {
    const map = new Map<string, { key: string; id: string | null; name: string }>();
    officers.forEach((o) => {
      const key = o.hospital_id || o.hospital_name || "unknown";
      if (!map.has(key)) map.set(key, { key, id: o.hospital_id ?? null, name: o.hospital_name || "Unknown location" });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  }, [officers]);
  const multiHospital = hospitals.length >= 2;

  // Stable colour per series. The pooled "All my locations" group stays a neutral
  // navy/slate; each hospital pulls its color from the SHARED canonical map
  // (utils/hospitalColors) so a location reads with the same color here as on the
  // Scan Map, the comparison chart, and the calendar.
  const colorMap = useMemo(() => {
    const mp = new Map<string, string>();
    mp.set(ALL_KEY, isDark ? "#CBD5E1" : "#0B1F3A");
    hospitals.forEach((h) => mp.set(h.key, hospitalColors.colorFor({ id: h.id, name: h.name })));
    return mp;
  }, [hospitals, isDark, hospitalColors]);

  // One series per hospital, plus a pooled "All my locations" series. Each keeps
  // its officers (for the hover tooltip) and its summary stats.
  const seriesList = useMemo(() => {
    const byVals = new Map<string, number[]>();
    const byOff = new Map<string, OfficerPerformanceRow[]>();
    const allVals: number[] = [];
    const allOff: OfficerPerformanceRow[] = [];
    officers.forEach((o) => {
      const v = md.value(o);
      if (v == null || isNaN(v)) return;
      allVals.push(v);
      allOff.push(o);
      const key = o.hospital_id || o.hospital_name || "unknown";
      if (!byVals.has(key)) { byVals.set(key, []); byOff.set(key, []); }
      byVals.get(key)!.push(v);
      byOff.get(key)!.push(o);
    });
    const mk = (key: string, name: string, vals: number[], off: OfficerPerformanceRow[]) => ({ key, name, officers: off, ...statsOf(vals) });
    const list = [mk(ALL_KEY, "All my locations", allVals, allOff)];
    hospitals.forEach((h) => list.push(mk(h.key, h.name, byVals.get(h.key) ?? [], byOff.get(h.key) ?? [])));
    return list;
  }, [officers, metric, hospitals]); // eslint-disable-line react-hooks/exhaustive-deps

  const toggleSel = (key: string) =>
    setSelected((prev) => { const n = new Set(prev); if (n.has(key)) n.delete(key); else n.add(key); return n; });

  // On entering compare mode (or when the hospital set changes) seed/repair the
  // selection so all locations are shown and the focus is valid.
  useEffect(() => {
    if (!compareOn) return;
    const valid = new Set<string>([ALL_KEY, ...hospitals.map((h) => h.key)]);
    setSelected((prev) => {
      const next = new Set([...prev].filter((k) => valid.has(k)));
      if (next.size === 0) valid.forEach((k) => next.add(k));
      return next;
    });
    setFocus((f) => (valid.has(f) ? f : ALL_KEY));
  }, [compareOn, hospitals]);

  const chart = useMemo(() => {
    if (S.n === 0) return null;
    const nb = bins;
    let lo = S.min;
    let hi = S.max;
    if (lo === hi) {
      lo -= 1;
      hi += 1;
    }
    const bw = (hi - lo) / nb;
    const binList = Array.from({ length: nb }, (_, k) => ({ x0: lo + k * bw, x1: lo + (k + 1) * bw, items: [] as OfficerPerformanceRow[] }));
    officers.forEach((o) => {
      const raw = md.value(o);
      if (raw == null || isNaN(raw)) return;
      let k = Math.floor((raw - lo) / bw);
      if (k >= nb) k = nb - 1;
      if (k < 0) k = 0;
      binList[k].items.push(o);
    });
    const maxCount = Math.max(1, ...binList.map((b) => b.items.length));
    const peak = S.std > 0 ? S.n * bw * (1 / (S.std * Math.sqrt(2 * Math.PI))) : maxCount;
    const yMax = Math.max(maxCount, curve ? peak : 0) * 1.15;

    const X = (v: number) => m.l + ((v - lo) / (hi - lo)) * iw;
    const Y = (c: number) => m.t + ih - (c / yMax) * ih;

    // normal curve path
    let curvePath = "";
    if (curve && S.std > 0) {
      const N = 120;
      for (let i = 0; i <= N; i++) {
        const xv = lo + (hi - lo) * (i / N);
        const yc = S.n * bw * (1 / (S.std * Math.sqrt(2 * Math.PI))) * Math.exp(-((xv - S.mean) ** 2) / (2 * S.std * S.std));
        curvePath += (i ? "L" : "M") + X(xv).toFixed(1) + " " + Y(yc).toFixed(1) + " ";
      }
    }

    // percentile markers — merge coincident values, stagger the labels
    const pdefs = [
      { v: q[1], name: "p25" },
      { v: q[2], name: "median" },
      { v: q[3], name: "p75" },
      { v: q[4], name: "p90" },
    ];
    const groups = new Map<string, { v: number; names: string[] }>();
    pdefs.forEach((p) => {
      const kv = fmtMetric(p.v, md.dec);
      const g = groups.get(kv) || { v: p.v, names: [] };
      g.names.push(p.name);
      groups.set(kv, g);
    });
    const clusters = Array.from(groups.values())
      .map((g) => ({ x: X(g.v), label: g.names.join(" · "), med: g.names.includes("median") }))
      .sort((a, b) => a.x - b.x);
    const rowY = [m.t - 9, m.t - 22];
    const rowEnd = [-1e9, -1e9];
    const labels = clusters.map((c) => {
      const w = c.label.length * 6.2 + 6;
      const start = c.x - w / 2;
      const end = c.x + w / 2;
      let row = start >= rowEnd[0] + 3 ? 0 : start >= rowEnd[1] + 3 ? 1 : rowEnd[0] <= rowEnd[1] ? 0 : 1;
      rowEnd[row] = Math.max(rowEnd[row], end);
      return { ...c, y: rowY[row] };
    });

    const yticks = 4;
    const xticks = 6;
    return { lo, hi, bw, binList, yMax, X, Y, curvePath, clusters, labels, yticks, xticks };
  }, [S, bins, curve, officers, metric]); // eslint-disable-line react-hooks/exhaustive-deps

  // Hovering a Summary statistic marks where it sits on the plot: a single
  // metric value (mean / a percentile) is a labelled vertical marker; a spread
  // (±1 SD, full range) is a shaded band between its two edges.
  const highlight = useMemo(() => {
    if (!hl || !chart || S.n === 0) return null;
    const P = (v: number, label: string) => ({ kind: "point" as const, v, label });
    const B = (lo: number, hi: number, label: string) => ({ kind: "band" as const, lo, hi, label });
    switch (hl) {
      case "mean": return P(S.mean, "Mean");
      case "median": return P(q[2], "Median");
      case "std": return B(S.mean - S.std, S.mean + S.std, "±1 SD");
      case "range": return B(S.min, S.max, "Range");
      case "p10": return P(q[0], "10th pct");
      case "p25": return P(q[1], "25th pct");
      case "p50": return P(q[2], "Median");
      case "p75": return P(q[3], "75th pct");
      case "p90": return P(q[4], "90th pct");
      default: return null;
    }
  }, [hl, chart, S, q]);

  // Compare mode: overlay each selected location's distribution on a shared
  // x-scale. Curves and translucent histograms are drawn as DENSITY (area = 1)
  // so shapes compare directly regardless of how many officers a location has.
  const compareChart = useMemo(() => {
    if (!compareOn || S.n === 0) return null;
    const nb = bins;
    let lo = S.min;
    let hi = S.max;
    if (lo === hi) { lo -= 1; hi += 1; }
    const bw = (hi - lo) / nb;
    const X = (v: number) => m.l + ((v - lo) / (hi - lo)) * iw;

    const drawn = seriesList.filter((s) => selected.has(s.key) && s.n > 0);
    const focusSeries = seriesList.find((s) => s.key === focus && s.n > 0) || drawn[0] || null;

    const binsFor = (off: OfficerPerformanceRow[], n: number) => {
      const arr = Array.from({ length: nb }, (_, k) => ({ x0: lo + k * bw, x1: lo + (k + 1) * bw, items: [] as OfficerPerformanceRow[] }));
      off.forEach((o) => {
        const raw = md.value(o);
        if (raw == null || isNaN(raw)) return;
        let k = Math.floor((raw - lo) / bw);
        if (k >= nb) k = nb - 1;
        if (k < 0) k = 0;
        arr[k].items.push(o);
      });
      return arr.map((b) => ({ ...b, dens: n > 0 ? b.items.length / n / bw : 0 }));
    };

    let yMax = 0;
    // Translucent histogram per selected location (focus drawn last / on top).
    const ordered = [...drawn].sort((a, b) => (a.key === focusSeries?.key ? 1 : 0) - (b.key === focusSeries?.key ? 1 : 0));
    const bars = ordered.map((s) => {
      const b = binsFor(s.officers, s.n);
      b.forEach((bin) => { if (bin.dens > yMax) yMax = bin.dens; });
      return { key: s.key, isFocus: s.key === focusSeries?.key, bins: b };
    });

    const curves = drawn.filter((s) => s.std > 0).map((s) => {
      const peak = 1 / (s.std * Math.sqrt(2 * Math.PI));
      if (peak > yMax) yMax = peak;
      return s;
    });
    yMax = (yMax || 1) * 1.18;
    const Y = (d: number) => m.t + ih - (d / yMax) * ih;

    const curvePaths = curves.map((s) => {
      let path = "";
      const N = 120;
      for (let j = 0; j <= N; j++) {
        const xv = lo + (hi - lo) * (j / N);
        const yc = (1 / (s.std * Math.sqrt(2 * Math.PI))) * Math.exp(-((xv - s.mean) ** 2) / (2 * s.std * s.std));
        path += (j ? "L" : "M") + X(xv).toFixed(1) + " " + Y(yc).toFixed(1) + " ";
      }
      return { key: s.key, name: s.name, mean: s.mean, isFocus: s.key === focusSeries?.key, path };
    });

    // Bins used for the hover tooltip: the focused series (with officer items).
    const binList = focusSeries ? binsFor(focusSeries.officers, focusSeries.n) : Array.from({ length: nb }, (_, k) => ({ x0: lo + k * bw, x1: lo + (k + 1) * bw, items: [] as OfficerPerformanceRow[], dens: 0 }));
    const groupMean = seriesList.find((s) => s.key === ALL_KEY)?.mean ?? S.mean;
    return { lo, hi, bw, bars, curvePaths, binList, yMax, X, Y, groupMean, focusKey: focusSeries?.key ?? null, yticks: 4, xticks: 6 };
  }, [compareOn, S, bins, seriesList, selected, focus, metric]); // eslint-disable-line react-hooks/exhaustive-deps

  // Which chart drives the shared axes / hover targets.
  const ac = compareOn && compareChart ? compareChart : chart;

  return (
    <div className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm dark:border-[#404040] dark:bg-[#2D2D2D]">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-b border-gray-100 px-4 py-3 dark:border-[#3A3A3A]">
        <span className="text-[11px] font-bold uppercase tracking-wide text-gray-400">Distribution</span>
        {open && (
          <>
            <div className="inline-flex flex-wrap gap-1 rounded-lg bg-gray-100 p-1 dark:bg-[#242424]">
              {OFFICER_METRICS.map((mm) => {
                const on = mm.key === metric;
                return (
                  <button
                    key={mm.key}
                    type="button"
                    onClick={() => onMetricChange(mm.key)}
                    className="rounded-md px-3 py-1.5 text-[13px] transition-all"
                    style={
                      on
                        ? { background: mm.color, color: mm.textOn, fontWeight: 700, transform: "scale(1.06)", boxShadow: `0 0 0 3px ${mm.color}33, 0 4px 12px ${mm.color}55` }
                        : { color: mm.textCol, fontWeight: 700 }
                    }
                  >
                    {mm.label}
                  </button>
                );
              })}
            </div>
            <label className="inline-flex cursor-pointer select-none items-center gap-1.5 text-[13px] text-gray-600 dark:text-gray-300">
              <input type="checkbox" checked={curve} onChange={(e) => setCurve(e.target.checked)} /> Bell curve
            </label>
            {multiHospital && (
              <label className="inline-flex cursor-pointer select-none items-center gap-1.5 text-[13px] font-medium text-gray-700 dark:text-gray-200">
                <input type="checkbox" checked={compareOn} onChange={(e) => setCompareOn(e.target.checked)} /> Compare locations
              </label>
            )}
          </>
        )}
        <span className="flex-1" />
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="text-xs font-semibold text-gray-500 transition-colors hover:text-gray-800 dark:text-gray-400 dark:hover:text-gray-100"
          aria-expanded={open}
        >
          {open ? "Collapse" : "Expand"}
        </button>
      </div>

      {open && (S.n === 0 ? (
        <div className="p-10 text-center text-sm text-gray-500 dark:text-gray-400">
          No {md.label.toLowerCase()} recorded for these officers in the window.
        </div>
      ) : (
        <div className="grid gap-4 p-4 lg:grid-cols-[1fr_300px]">
          {/* chart */}
          <div>
            <p className="mb-1 text-sm font-semibold text-gray-800 dark:text-white">
              {md.label} — {compareOn ? `${hospitals.length} locations, ${S.n} officers` : `${S.n} officers`}
            </p>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">
              {compareOn
                ? "Each location as its own translucent histogram and fitted curve, drawn as density so shapes compare directly regardless of headcount. The dashed line is the average across all your locations."
                : `Bars = officers in each ${md.label.toLowerCase()} range; the curve is the fitted normal (bell) distribution. Dashed lines mark percentiles.`}
            </p>

            {/* Overlay legend — toggle a location's curve/bars; the ▮ chip picks
                which location's bars carry the hover tooltip. */}
            {compareOn && (
              <div className="mb-2 flex flex-wrap items-center gap-1.5">
                <span className="mr-1 text-[11px] font-bold uppercase tracking-wide text-gray-400">Overlays</span>
                {seriesList.map((s) => {
                  const color = colorMap.get(s.key) ?? "#888";
                  const on = selected.has(s.key);
                  const isFocus = (compareChart?.focusKey ?? null) === s.key;
                  return (
                    <div
                      key={s.key}
                      className={cn(
                        "inline-flex items-center overflow-hidden rounded-full border text-[12px]",
                        on ? "border-gray-300 dark:border-[#4a4a4a]" : "border-dashed border-gray-300 opacity-50 dark:border-[#4a4a4a]"
                      )}
                    >
                      <button
                        type="button"
                        onClick={() => toggleSel(s.key)}
                        className="inline-flex items-center gap-1.5 py-1 pl-2 pr-1.5 hover:bg-gray-100 dark:hover:bg-[#333]"
                        title={on ? "Hide this location" : "Show this location"}
                      >
                        <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: color }} />
                        <span className={cn("font-semibold", s.key === ALL_KEY ? "text-gray-900 dark:text-white" : "text-gray-700 dark:text-gray-200")}>{s.name}</span>
                        <span className="text-gray-400">{s.officers.length}</span>
                      </button>
                      <button
                        type="button"
                        onClick={() => { setSelected((p) => new Set(p).add(s.key)); setFocus(s.key); }}
                        title="Show histogram bars for this location"
                        className={cn(
                          "border-l px-1.5 py-1 text-[11px] leading-none border-gray-200 dark:border-[#4a4a4a]",
                          isFocus ? "bg-gray-800 text-white dark:bg-gray-200 dark:text-gray-900" : "text-gray-400 hover:text-gray-700 dark:hover:text-gray-200"
                        )}
                      >
                        ▮
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            {ac && (
              <div className="relative" ref={wrapRef}>
              <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ overflow: "visible" }} role="img" aria-label={`${md.label} distribution`}>
                {/* Soft outer glow (in the metric's color) for the active bar. */}
                <defs>
                  <filter id="barGlow" x="-40%" y="-40%" width="180%" height="180%">
                    <feDropShadow dx="0" dy="0" stdDeviation="4" floodColor={md.color} floodOpacity="0.6" />
                  </filter>
                </defs>
                {/* y grid + ticks */}
                {Array.from({ length: ac.yticks + 1 }, (_, i) => {
                  const c = (ac.yMax * i) / ac.yticks;
                  const y = ac.Y(c);
                  return (
                    <g key={`y${i}`}>
                      <line x1={m.l} y1={y} x2={m.l + iw} y2={y} stroke={grid} strokeWidth={1} />
                      {!compareOn && <text x={m.l - 8} y={y + 4} textAnchor="end" fontSize={11} fill={muted}>{Math.round(c)}</text>}
                    </g>
                  );
                })}
                {/* ── single mode: bars + curve + percentiles + highlight ── */}
                {/* bars — the active (hovered/pinned) bar keeps its color but
                    grows a little and gains a light glowing ring in that same
                    color, mirroring the selected metric button. */}
                {!compareOn && chart && chart.binList.map((b, i) => {
                  const x = chart.X(b.x0) + 1.2;
                  const w = Math.max(1, chart.X(b.x1) - chart.X(b.x0) - 2.4);
                  const c = b.items.length;
                  const y = chart.Y(c);
                  const h = Math.max(0, m.t + ih - y);
                  if (active?.bin === i) {
                    // Bigger: grow ~3px each side, extending upward while the base
                    // stays on the axis.
                    const gx = x - 3, gw = w + 6, gy = y - 3, gh = h + 3;
                    return (
                      <g key={`b${i}`}>
                        {/* light ring / halo */}
                        <rect x={gx - 2} y={gy - 2} width={gw + 4} height={gh + 4} rx={6} ry={6} fill={md.color} opacity={0.2} />
                        {/* the bar itself: full color, crisp ring, soft glow */}
                        <rect x={gx} y={gy} width={gw} height={gh} rx={5} ry={5} fill={md.color} fillOpacity={1} stroke={md.color} strokeWidth={2} filter="url(#barGlow)" />
                      </g>
                    );
                  }
                  return <rect key={`b${i}`} x={x} y={y} width={w} height={h} rx={3} ry={3} fill={md.color} fillOpacity={0.92} />;
                })}
                {/* normal curve */}
                {!compareOn && chart && chart.curvePath && <path d={chart.curvePath} fill="none" stroke={curveColor} strokeWidth={2.2} strokeLinejoin="round" opacity={0.9} />}
                {/* percentile lines */}
                {!compareOn && chart && chart.clusters.map((c, i) => (
                  <line key={`pl${i}`} x1={c.x} y1={m.t} x2={c.x} y2={m.t + ih} stroke={c.med ? "#C9A227" : "#9AA5B1"} strokeWidth={c.med ? 2 : 1.4} strokeDasharray={c.med ? "0" : "4 3"} />
                ))}
                {!compareOn && chart && chart.labels.map((c, i) => (
                  <text key={`plab${i}`} x={c.x} y={c.y} textAnchor="middle" fontSize={10.5} fontWeight={700} fill={c.med ? "#9a7d10" : muted}>{c.label}</text>
                ))}

                {/* ── compare mode: translucent per-location histograms, group
                    average line, then fitted density curves on top ── */}
                {compareOn && compareChart && (
                  <g>
                    {compareChart.bars.map((series) => {
                      const col = colorMap.get(series.key) ?? "#94a3b8";
                      return (
                        <g key={`cbars${series.key}`}>
                          {series.bins.map((b, i) => {
                            const x = compareChart.X(b.x0) + 1;
                            const w = Math.max(1, compareChart.X(b.x1) - compareChart.X(b.x0) - 2);
                            const y = compareChart.Y(b.dens);
                            const h = Math.max(0, m.t + ih - y);
                            if (h <= 0) return null;
                            const hot = series.isFocus && active?.bin === i;
                            return <rect key={`cb${series.key}-${i}`} x={x} y={y} width={w} height={h} rx={2.5} ry={2.5} fill={col} fillOpacity={series.isFocus ? (hot ? 0.55 : 0.34) : 0.16} />;
                          })}
                        </g>
                      );
                    })}
                    {/* group-average reference line */}
                    {(() => {
                      const gx = Math.max(m.l, Math.min(m.l + iw, compareChart.X(compareChart.groupMean)));
                      const col = isDark ? "#CBD5E1" : "#0B1F3A";
                      return (
                        <g pointerEvents="none">
                          <line x1={gx} y1={m.t} x2={gx} y2={m.t + ih} stroke={col} strokeWidth={1.6} strokeDasharray="5 3" opacity={0.8} />
                          <text x={Math.max(m.l + 34, Math.min(m.l + iw - 34, gx))} y={m.t - 5} textAnchor="middle" fontSize={10} fontWeight={700} fill={col}>
                            Group avg · {fmtMetric(compareChart.groupMean, md.dec)}{md.unit}
                          </text>
                        </g>
                      );
                    })()}
                    {compareChart.curvePaths.map((cp) => (
                      <path key={`cc${cp.key}`} d={cp.path} fill="none" stroke={colorMap.get(cp.key) ?? "#888"} strokeWidth={cp.isFocus ? 3 : 2} strokeLinejoin="round" opacity={0.95} />
                    ))}
                  </g>
                )}

                {/* Summary-stat highlight: a labelled marker (point) or shaded
                    band (spread), in the metric's color, on top of the bars. */}
                {!compareOn && chart && highlight && (() => {
                  const clampX = (v: number) => Math.max(m.l, Math.min(m.l + iw, chart.X(v)));
                  const chipH = 20;
                  const chipY = m.t + 4;
                  const chipText =
                    highlight.kind === "point"
                      ? `${highlight.label} · ${fmtMetric(highlight.v, md.dec)}${md.unit}`
                      : `${highlight.label} · ${fmtMetric(highlight.lo, md.dec)}–${fmtMetric(highlight.hi, md.dec)}${md.unit}`;
                  const chipW = chipText.length * 6.2 + 20;
                  const chipCx = (cx: number) => Math.max(m.l + chipW / 2, Math.min(m.l + iw - chipW / 2, cx));
                  // Each vertical is drawn twice: a white "casing" underneath so
                  // the colored marker stays visible even where it crosses a
                  // same-colored bar, then the colored line on top.
                  const vline = (x: number, key: string) => (
                    <g key={key}>
                      <line x1={x} y1={m.t} x2={x} y2={m.t + ih} stroke="#fff" strokeWidth={5.5} strokeOpacity={0.95} />
                      <line x1={x} y1={m.t} x2={x} y2={m.t + ih} stroke={md.color} strokeWidth={2.5} />
                    </g>
                  );
                  const chip = (cx: number) => {
                    const x = chipCx(cx);
                    return (
                      <g>
                        <rect x={x - chipW / 2} y={chipY} width={chipW} height={chipH} rx={5} ry={5} fill={md.color} stroke="#fff" strokeWidth={1.5} />
                        <text x={x} y={chipY + 14} textAnchor="middle" fontSize={11} fontWeight={700} fill={md.textOn}>{chipText}</text>
                      </g>
                    );
                  };
                  if (highlight.kind === "point") {
                    const x = clampX(highlight.v);
                    return (
                      <g pointerEvents="none">
                        {/* translucent spotlight column so the marker reads as a
                            clear "here" even at a glance */}
                        <rect x={x - 8} y={m.t} width={16} height={ih} fill={md.color} opacity={0.16} />
                        {vline(x, "v")}
                        <circle cx={x} cy={m.t + ih} r={5} fill={md.color} stroke="#fff" strokeWidth={2} />
                        {chip(x)}
                      </g>
                    );
                  }
                  const xlo = clampX(highlight.lo);
                  const xhi = clampX(highlight.hi);
                  return (
                    <g pointerEvents="none">
                      <rect x={xlo} y={m.t} width={Math.max(1, xhi - xlo)} height={ih} fill={md.color} opacity={0.18} />
                      {vline(xlo, "lo")}
                      {vline(xhi, "hi")}
                      {chip((xlo + xhi) / 2)}
                    </g>
                  );
                })()}
                {/* x axis */}
                <line x1={m.l} y1={m.t + ih} x2={m.l + iw} y2={m.t + ih} stroke={axis} strokeWidth={1} />
                {Array.from({ length: ac.xticks + 1 }, (_, i) => {
                  const xv = ac.lo + (ac.hi - ac.lo) * (i / ac.xticks);
                  return (
                    <text key={`x${i}`} x={ac.X(xv)} y={m.t + ih + 18} textAnchor="middle" fontSize={11} fill={muted}>{fmtMetric(xv, md.dec)}</text>
                  );
                })}
                <text x={m.l + iw / 2} y={H - 6} textAnchor="middle" fontSize={12} fontWeight={600} fill={text}>{md.label}{md.unit ? ` (${md.unit.trim()})` : ""}</text>
                <text transform={`translate(14 ${m.t + ih / 2}) rotate(-90)`} textAnchor="middle" fontSize={12} fontWeight={600} fill={text}>{compareOn ? "Relative density" : "Officers"}</text>
                {/* Full-height hover/click targets so short bars are still easy to hit. */}
                {ac.binList.map((b, i) => (
                  <rect
                    key={`hit${i}`}
                    x={ac.X(b.x0)}
                    y={m.t}
                    width={Math.max(1, ac.X(b.x1) - ac.X(b.x0))}
                    height={ih}
                    fill="transparent"
                    onMouseMove={(e) => setHover({ bin: i, ...posFromEvent(e) })}
                    onMouseLeave={() => setHover(null)}
                  />
                ))}
              </svg>
              {active && ac.binList[active.bin] && (
                <div
                  className={cn(
                    "pointer-events-none absolute z-20 max-h-[380px] overflow-hidden rounded-lg border border-gray-200 bg-white p-2 text-xs shadow-xl dark:border-[#4a4a4a] dark:bg-[#242424]",
                    compareOn ? "w-[264px]" : "w-[232px]"
                  )}
                  style={{ left: Math.max(4, Math.min(active.left + 14, active.w - (compareOn ? 272 : 240))), top: active.top + 12 }}
                >
                  {(() => {
                    const b = ac.binList[active.bin];
                    const rangeLabel = `${fmtMetric(b.x0, md.dec)}–${fmtMetric(b.x1, md.dec)}${md.unit}`;

                    // Compare mode: group the officers in this bar BY LOCATION so a
                    // reader can see, at a glance, how many officers from each site
                    // land in this range — each site keyed by its own colour.
                    if (compareOn && compareChart) {
                      const loB = compareChart.lo;
                      const bwB = compareChart.bw;
                      const nb = bins;
                      const inThisBin = (o: OfficerPerformanceRow) => {
                        const v = md.value(o);
                        if (v == null || isNaN(v)) return false;
                        let k = Math.floor((v - loB) / bwB);
                        if (k >= nb) k = nb - 1;
                        if (k < 0) k = 0;
                        return k === active.bin;
                      };
                      // Only break down the locations currently selected as overlays,
                      // so the tooltip matches what's highlighted on the plot.
                      const groups = hospitals
                        .filter((h) => selected.has(h.key))
                        .map((h) => {
                          const ser = seriesList.find((s) => s.key === h.key);
                          return { key: h.key, name: h.name, color: colorMap.get(h.key) ?? "#888", items: (ser?.officers ?? []).filter(inThisBin) };
                        })
                        .filter((g) => g.items.length > 0)
                        .sort((a2, b2) => b2.items.length - a2.items.length);
                      const total = groups.reduce((n, g) => n + g.items.length, 0);
                      return (
                        <>
                          <div className="mb-1 font-bold text-gray-900 dark:text-white">
                            {rangeLabel}
                            <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">· {total} officer{total === 1 ? "" : "s"}</span>
                          </div>
                          {total === 0 ? (
                            <div className="text-gray-400">No officers in this range.</div>
                          ) : (
                            <div className="space-y-1.5">
                              {groups.map((g) => {
                                const shown = g.items.slice(0, 5);
                                return (
                                  <div key={g.key} className="pl-2" style={{ borderLeft: `3px solid ${g.color}` }}>
                                    <div className="flex items-center justify-between gap-2">
                                      <span className="flex min-w-0 items-center gap-1.5 font-semibold text-gray-800 dark:text-gray-100">
                                        <span className="h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: g.color }} />
                                        <span className="truncate">{g.name}</span>
                                      </span>
                                      <span className="flex-shrink-0 rounded-full px-1.5 text-[11px] font-bold leading-tight text-white" style={{ background: g.color }}>{g.items.length}</span>
                                    </div>
                                    <div className="mt-0.5 space-y-0.5 pl-4">
                                      {shown.map((o) => (
                                        <div key={o.officer_id} className="flex items-center justify-between gap-2">
                                          <span className="truncate text-gray-600 dark:text-gray-300">{o.name}</span>
                                          <span className="flex-shrink-0 font-semibold text-gray-900 dark:text-white">{fmtMetric(md.value(o) ?? 0, md.dec)}{md.unit}</span>
                                        </div>
                                      ))}
                                      {g.items.length > shown.length && (
                                        <div className="text-gray-400">+{g.items.length - shown.length} more</div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          )}
                        </>
                      );
                    }

                    // Single mode: a flat list of the officers in this bar.
                    const items = b.items;
                    const shown = items.slice(0, 12);
                    return (
                      <>
                        <div className="mb-1 font-bold text-gray-900 dark:text-white">
                          {rangeLabel}
                          <span className="ml-1 font-normal text-gray-500 dark:text-gray-400">· {items.length} officer{items.length === 1 ? "" : "s"}</span>
                        </div>
                        {items.length === 0 ? (
                          <div className="text-gray-400">—</div>
                        ) : (
                          <div className="space-y-0.5">
                            {shown.map((o) => (
                              <div key={o.officer_id} className="flex items-center justify-between gap-2">
                                <span className="truncate text-gray-700 dark:text-gray-200">{o.name}</span>
                                <span className="flex-shrink-0 font-semibold text-gray-900 dark:text-white">
                                  {fmtMetric(md.value(o) ?? 0, md.dec)}{md.unit}
                                </span>
                              </div>
                            ))}
                            {items.length > shown.length && (
                              <div className="pt-0.5 text-gray-400">+{items.length - shown.length} more</div>
                            )}
                          </div>
                        )}
                      </>
                    );
                  })()}
                </div>
              )}
              </div>
            )}
            <div className="mt-1 flex items-center justify-center gap-2 text-xs text-gray-500 dark:text-gray-400">
              <span className="font-semibold uppercase tracking-wide">Bin width</span>
              <input type="range" min={6} max={16} value={bins} onChange={(e) => setBins(Number(e.target.value))} style={{ width: 160 }} />
              <span><b>{bins}</b> bins</span>
            </div>
          </div>

          {/* summary */}
          {compareOn ? (
          <div>
            <p className="mb-1 text-sm font-semibold text-gray-800 dark:text-white">Comparison</p>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">{md.label} per location — mean, median, spread</p>
            {/* Compact stacked cards (not a wide table) so every stat stays
                visible inside the narrow side panel at any window width — long
                names wrap instead of pushing columns off the edge. */}
            <div className="space-y-1">
              {seriesList.filter((s) => selected.has(s.key) && s.n > 0).map((s) => {
                const isFocus = (compareChart?.focusKey ?? null) === s.key;
                const stat = (label: string, value: string, strong = false) => (
                  <span className="whitespace-nowrap">
                    <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">{label} </span>
                    <span className={strong ? "font-bold text-gray-900 dark:text-white" : "text-gray-600 dark:text-gray-300"}>{value}</span>
                  </span>
                );
                return (
                  <button
                    type="button"
                    key={s.key}
                    onClick={() => setFocus(s.key)}
                    title="Show this location's histogram bars"
                    className={cn(
                      "w-full rounded-lg border px-2.5 py-2 text-left transition-colors",
                      isFocus
                        ? "border-gray-300 bg-gray-50 dark:border-[#4a4a4a] dark:bg-[#242424]"
                        : "border-transparent hover:bg-gray-50 dark:hover:bg-[#242424]"
                    )}
                  >
                    <div className="flex items-start gap-1.5">
                      <span className="mt-1 h-2.5 w-2.5 flex-shrink-0 rounded-full" style={{ background: colorMap.get(s.key) ?? "#888" }} />
                      <span className={cn("text-[12.5px] leading-snug", isFocus ? "font-bold text-gray-900 dark:text-white" : "font-semibold text-gray-700 dark:text-gray-200")}>
                        {s.name}
                        {isFocus && <span className="ml-1 align-middle text-[10px] font-semibold uppercase tracking-wide text-gray-400">▮ bars</span>}
                      </span>
                    </div>
                    <div className="mt-1 flex flex-wrap items-baseline gap-x-2.5 gap-y-0.5 pl-4 text-[11.5px]">
                      {stat("mean", `${fmtMetric(s.mean, md.dec)}${md.unit}`, true)}
                      {stat("med", `${fmtMetric(s.q[2], md.dec)}${md.unit}`)}
                      {stat("sd", fmtMetric(s.std, md.dec))}
                      {stat("n", String(s.n))}
                    </div>
                  </button>
                );
              })}
            </div>
            <p className="mt-3 text-[11px] leading-relaxed text-gray-400">
              Dashed line = average across all your locations. Bars show the highlighted (▮) location; curves are each location's fitted distribution, scaled to the same area so shapes compare directly. Click a location to switch which one's bars show.
            </p>
          </div>
          ) : (
          <div>
            <p className="mb-1 text-sm font-semibold text-gray-800 dark:text-white">Summary</p>
            <p className="mb-2 text-xs text-gray-500 dark:text-gray-400">Selected metric across {S.n} officers</p>
            <div className="grid grid-cols-2 gap-2">
              {[
                ["mean", "Mean", fmtMetric(S.mean, md.dec) + md.unit],
                ["median", "Median", fmtMetric(q[2], md.dec) + md.unit],
                ["std", "Std dev", fmtMetric(S.std, md.dec)],
                ["range", "Range", `${fmtMetric(S.min, md.dec)}–${fmtMetric(S.max, md.dec)}`],
              ].map(([hk, k, v]) => (
                <div
                  key={k}
                  onMouseEnter={() => setHl(hk)}
                  onMouseLeave={() => setHl(null)}
                  className="cursor-default rounded-lg bg-gray-50 px-3 py-2 transition-shadow dark:bg-[#242424]"
                  style={hl === hk ? { boxShadow: `0 0 0 2px ${md.color}`, background: `${md.color}14` } : undefined}
                >
                  <div className="text-[10px] font-bold uppercase tracking-wide text-gray-500 dark:text-gray-400">{k}</div>
                  <div className="text-lg font-bold text-gray-900 dark:text-white">{v}</div>
                </div>
              ))}
            </div>
            <table className="mt-3 w-full text-[12.5px]">
              <tbody>
                {[
                  ["p10", "10th percentile", q[0]],
                  ["p25", "25th percentile", q[1]],
                  ["p50", "50th (median)", q[2]],
                  ["p75", "75th percentile", q[3]],
                  ["p90", "90th percentile", q[4]],
                ].map(([hk, label, v]) => (
                  <tr
                    key={label as string}
                    onMouseEnter={() => setHl(hk as string)}
                    onMouseLeave={() => setHl(null)}
                    className="cursor-default border-b border-gray-100 transition-colors dark:border-[#3A3A3A]"
                    style={hl === hk ? { background: `${md.color}1F` } : undefined}
                  >
                    <td className="py-1 text-gray-600 dark:text-gray-300">{label}</td>
                    <td className="py-1 text-right font-bold text-gray-900 dark:text-white">
                      {fmtMetric(v as number, md.dec)}
                      {md.unit}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          )}
        </div>
      ))}
    </div>
  );
}

/** Small percentile badge, shared by the officer table. */
export function PercentileBadge({ p }: { p: number | null }) {
  if (p == null) return <span className="text-gray-400">—</span>;
  const st = pctStyle(p);
  return (
    <span className="inline-block rounded-full px-2.5 py-0.5 text-[11.5px] font-bold" style={{ background: st.bg, color: st.fg }}>
      {ordinal(p)}
    </span>
  );
}

// avoid an "unused React import" error under the classic JSX runtime
void React;
